// Organisation setting "Documents request in the KYC form" (settings.documentsRequest):
//   individuals: "none" | "start" | "end"   (start: upload ID + proof of address first, details pre-filled)
//   individualsCv: true                      (start only: also ask for a CV)
//   entities: "none" | "start" | "end"      (start: upload the registration document first, details pre-filled)
//   entitiesDocTypes: ["registration", ...] (types of corporate documents requested, from the list of the
//                                            country of incorporation; at least one, default registration)
//   maxPages: 1 to 10 (default 4)          (pages of an uploaded PDF read by the AI; the first ones only)
// "end" = the client uploads at the end of the form, now or later (documents later link).
// An organisation not yet saved in this format is read from its former single setting (mode).
// Same logic in the APIs: MYKYC_API/myKyc_documents_request.js

export const DOCUMENT_REQUEST_TIMINGS = [
  { value: "none", label: "Not requested", hint: "Documents are requested separately by staff" },
  { value: "start", label: "At the start", hint: "Upload first; details are pre-filled from the documents" },
  { value: "end", label: "At the end", hint: "After completing the form, upload now or later" },
];
const TIMING_VALUES = DOCUMENT_REQUEST_TIMINGS.map((t) => t.value);

// Corporate document types an organisation can request from entities (corporateDocumentRequirements types).
export const ENTITY_DOCUMENT_TYPES = [
  { value: "registration", label: "Registration", hint: "Register extract, certificate of incorporation, trade licence" },
  { value: "good_standing", label: "Good standing", hint: "Certificate of good standing or compliance" },
  { value: "constitutional", label: "Constitutional", hint: "Memorandum / articles of association, bylaws" },
  { value: "ownership", label: "Ownership", hint: "Register of members and directors, share register, incumbency" },
];

// Known types in their display order; none valid -> registration only.
export function entityDocTypes(value) {
  const list = Array.isArray(value) ? value : [];
  const types = ENTITY_DOCUMENT_TYPES.map((t) => t.value).filter((type) => list.includes(type));
  return types.length ? types : ["registration"];
}
const FIRST_INTAKE_MODE = "with_kyc_individuals_first_prompt";
const FIRST_INTAKE_CV_MODE = "with_kyc_individuals_first_prompt_cv";

const timing = (value) => (TIMING_VALUES.includes(value) ? value : "none");

export const INTAKE_MAX_PAGES_DEFAULT = 4;
// Pages of an uploaded PDF the AI reads (1 to 10), the default when not set.
export function intakeMaxPages(value) {
  const pages = Number(value);
  return Number.isInteger(pages) && pages >= 1 && pages <= 10 ? pages : INTAKE_MAX_PAGES_DEFAULT;
}

export function documentsRequestConfig(settings) {
  const dr = settings?.documentsRequest || {};
  if (TIMING_VALUES.includes(dr.individuals) || TIMING_VALUES.includes(dr.entities)) {
    const individuals = timing(dr.individuals);
    return {
      individuals,
      individualsCv: individuals === "start" && dr.individualsCv === true,
      entities: timing(dr.entities),
      entitiesDocTypes: entityDocTypes(dr.entitiesDocTypes),
      maxPages: intakeMaxPages(dr.maxPages),
    };
  }
  const mode = String(dr.mode || "");
  return {
    individuals: mode === "with_kyc_individuals_prompt" ? "end"
      : mode === FIRST_INTAKE_MODE || mode === FIRST_INTAKE_CV_MODE ? "start" : "none",
    individualsCv: mode === FIRST_INTAKE_CV_MODE,
    entities: "none",
    entitiesDocTypes: ["registration"],
    maxPages: intakeMaxPages(dr.maxPages),
  };
}

// Short description for tooltips, e.g. "Individuals: at the start (+ CV) · Entities: not requested".
export function documentsRequestSummary(config) {
  const label = (value) => DOCUMENT_REQUEST_TIMINGS.find((t) => t.value === value)?.label.toLowerCase() || "not requested";
  const types = config.entities !== "none"
    ? ` (${entityDocTypes(config.entitiesDocTypes).map((v) => ENTITY_DOCUMENT_TYPES.find((t) => t.value === v).label.toLowerCase()).join(", ")})`
    : "";
  return `Individuals: ${label(config.individuals)}${config.individualsCv ? " (+ CV)" : ""} · Entities: ${label(config.entities)}${types}`;
}

// Title of the entity documents step: "Company Documents" when constitutional / ownership documents are
// also requested, otherwise "Company Registration Documents".
export function entityDocumentsTitle(config) {
  return entityDocTypes(config?.entitiesDocTypes).some((t) => t === "constitutional" || t === "ownership")
    ? "Company Documents" : "Company Registration Documents";
}

// Former single value, still written for older app versions.
export function legacyDocumentsRequestMode(config) {
  if (config.individuals === "end") return "with_kyc_individuals_prompt";
  if (config.individuals === "start") return config.individualsCv ? FIRST_INTAKE_CV_MODE : FIRST_INTAKE_MODE;
  return "always_separate";
}
